import { DateTime } from "luxon";

import { IJob, JobPartCategory } from "../../models/Job";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import Card from "../shared/Card/Card";
import VehiclePartSummary from "./VehiclePartSummary";
import ContactFromId from "../contacts/ContactFromId";

interface JobOverviewCardProps {
  job?: IJob;
  jobPartCategories: JobPartCategory[];
}

export default function JobOverviewCard({ job, jobPartCategories }: JobOverviewCardProps) {
  const jobStatus = job?.completedOn || job?.dismantlingEnd
    ? "Completed"
    : job?.jobPartCategories.some((cat) => cat.partData?.removedOn !== null)
      ? "In Progress"
      : "Not Started";

  return (
    <Card bodyClassName="p-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[2fr_360px]">
        <div className="space-y-4 px-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                {/* replace is for old title that had duration baked in */}
                <h1 className="text-xl font-medium">{job?.title.replace(/-[^-]*$/, "")}</h1>
                <span
                  className={classNames(
                    "rounded-full px-2.5 py-1 text-xs font-semibold",
                    jobStatus === "Completed" && "bg-green-100 text-green-700",
                    jobStatus === "In Progress" && "bg-blue-100 text-blue-700",
                    jobStatus === "Not Started" && "bg-gray-100 text-gray-600",
                  )}
                >
                  {jobStatus}
                </span>
              </div>
              {job?.lead?.vehicle?.vrm && <h1 className="text-lg">{job?.lead?.vehicle?.vrm}</h1>}
            </div>
          </div>

          <p className="text-sm text-gray-700">{job?.description}</p>

          <div className="flex flex-wrap items-start gap-x-12 gap-y-3">
            <div className="min-w-[160px]">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Assigned To</p>
              <p className="text-sm font-medium text-gray-800">{job?.mechanicId ? <ContactFromId contactId={job.mechanicId} /> : "Not Allocated"}</p>
            </div>
            <div className="min-w-[160px]">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Created</p>
              <p className="text-sm font-medium text-gray-800">
                {job?.created ? formatDateTime(job.created, DateTime.DATE_MED) : "-"}
              </p>
            </div>
            <div className="min-w-[160px]">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Scheduled</p>
              <p className="text-sm font-medium text-gray-800">
                {job?.startDate ? formatDateTime(job.startDate, DateTime.DATETIME_SHORT) : "-"}
              </p>
            </div>
            <div className="min-w-[160px]">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Completed</p>
              <p className="text-sm font-medium text-gray-800">
                {job?.completedOn
                  ? formatDateTime(job.completedOn, DateTime.DATETIME_SHORT)
                  : job?.dismantlingEnd
                    ? formatDateTime(job.dismantlingEnd, DateTime.DATETIME_SHORT)
                    : "-"}
              </p>
            </div>
          </div>
        </div>

        <VehiclePartSummary jobPartCategories={jobPartCategories} />
      </div>
    </Card>
  );
}
